import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExternalLinkAlt, faCopy } from '@fortawesome/free-solid-svg-icons';
import './PaymentLinksModal.css';
import groupService from '../../api/groupService';
import { showMobileToast } from '../../utils/linkUtils';

const PaymentLinksModal = ({ isOpen, onClose, groupId, eventId, isClass = false, isGroupDonation = false, eventTitle = '' }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [paymentLinks, setPaymentLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [copyNotification, setCopyNotification] = useState('');
  const [sponsorshipAmount, setSponsorshipAmount] = useState(null);
  const [allowSponsorship, setAllowSponsorship] = useState(false);
  const modalRef = useRef(null);
  const overlayRef = useRef(null);

  // Payment service configurations with logos
  const paymentServices = {
    paypal: {
      name: 'PayPal',
      logo: 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/PaymentLogos/paypal%20logo.png',
      color: '#0070ba'
    },
    venmo: {
      name: 'Venmo',
      logo: 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/PaymentLogos/venmologo.png',
      color: '#008cff'
    },
    cashapp: {
      name: 'Cash App',
      logo: 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/PaymentLogos/cashapplogo.png',
      color: '#00d632'
    },
    zelle: {
      name: 'Zelle',
      logo: 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/PaymentLogos/zellelogo.png',
      color: '#6c1c5c'
    },
    other: {
      name: 'Other Payment',
      logo: 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/PaymentLogos/otherlogo.jpg',
      color: '#666666'
    }
  };

  // Fetch payment links when modal opens
  useEffect(() => {
    if (isOpen && groupId) {
      if (isGroupDonation) {
        fetchGroupPaymentLinks();
      } else if (eventId) {
        fetchPaymentLinks();
      }
    }
  }, [isOpen, groupId, eventId, isGroupDonation]);

  const fetchPaymentLinks = async () => {
    try {
      setLoading(true);
      let response;
      
      if (isClass) {
        response = await groupService.getClass(groupId, eventId);
      } else {
        response = await groupService.getEvent(groupId, eventId);
      }
      
      if (response && response.data) {
        // Set payment links
        if (response.data.payment_links) {
          setPaymentLinks(response.data.payment_links);
        }
        
        // Set sponsorship data
        setAllowSponsorship(response.data.allow_sponsorship || false);
        setSponsorshipAmount(response.data.sponsorship_amount || null);
      }
    } catch (error) {
      console.error("Error fetching payment links:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupPaymentLinks = async () => {
    try {
      setLoading(true);
      const response = await groupService.getGroup(groupId);
      
      if (response && response.data && response.data.payment_links) {
        setPaymentLinks(response.data.payment_links);
      }
    } catch (error) {
      console.error("Error fetching group payment links:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle modal animations
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
      
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.classList.add('active');
        }
        if (overlayRef.current) {
          overlayRef.current.classList.add('active');
        }
      }, 10);
    } else {
      setIsClosing(false);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle closing with animation
  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
    }
    
    setIsClosing(true);
    
    if (modalRef.current) {
      modalRef.current.classList.remove('active');
    }
    
    if (overlayRef.current) {
      overlayRef.current.classList.remove('active');
    }
    
    setTimeout(() => {
      onClose(e);
      setIsClosing(false);
    }, 300);
  };

  const handleClickOutside = (e) => {
    e.stopPropagation();
    
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      handleClose(e);
    }
  };

  const handleModalContentClick = (e) => {
    e.stopPropagation();
  };

  // Helper function to detect if a string is an email
  const isEmail = (str) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(str);
  };

  // Helper function to detect if a string is a phone number
  const isPhoneNumber = (str) => {
    // Remove all non-digit characters to check
    const digitsOnly = str.replace(/\D/g, '');
    // Check if it's a valid phone number (10-15 digits)
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  };

  // Helper function to detect if a string is a URL
  const isUrl = (str) => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  // Copy to clipboard function
  const copyToClipboard = async (text, service) => {
    try {
      await navigator.clipboard.writeText(text);
      const serviceConfig = paymentServices[service];
      
      showMobileToast(`${serviceConfig.name} info copied!`);
      
      // Clear notification after 3 seconds
      setTimeout(() => {
        setCopyNotification('');
      }, 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      
      // Fallback method
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          const serviceConfig = paymentServices[service];
          showMobileToast(`${serviceConfig.name} info copied!`);
          setTimeout(() => {
            setCopyNotification('');
          }, 3000);
        } else {
          setCopyNotification('Failed to copy to clipboard');
          setTimeout(() => {
            setCopyNotification('');
          }, 3000);
        }
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
        setCopyNotification('Failed to copy to clipboard');
        setTimeout(() => {
          setCopyNotification('');
        }, 3000);
      }
    }
  };

  // Handle payment link click
  const handlePaymentLinkClick = (service, link) => {
    // Check if it's an email or phone number
    if (isEmail(link) || isPhoneNumber(link)) {
      // Copy to clipboard instead of opening link
      copyToClipboard(link, service);
    } else if (isUrl(link)) {
      // Open the payment link in a new tab
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      // Treat as text to copy (fallback)
      copyToClipboard(link, service);
    }
  };

  // Get appropriate action text and icon based on link type
  const getActionInfo = (link) => {
    if (isEmail(link)) {
      return { text: 'Tap to copy email', icon: faCopy };
    } else if (isPhoneNumber(link)) {
      return { text: 'Tap to copy number', icon: faCopy };
    } else if (isUrl(link)) {
      return { text: 'Tap to pay', icon: faExternalLinkAlt };
    } else {
      return { text: 'Tap to copy', icon: faCopy };
    }
  };

  // Filter out empty payment links and sort them (other always last)
  const availablePayments = Object.entries(paymentLinks)
    .filter(([service, link]) => link && link.trim() !== '')
    .sort(([serviceA], [serviceB]) => {
      // Always put "other" at the end
      if (serviceA === 'other') return 1;
      if (serviceB === 'other') return -1;
      return 0;
    });

  if (!isOpen && !isClosing) return null;

  return ReactDOM.createPortal(
    <div className="payment-links-modal-overlay" ref={overlayRef} onClick={handleClickOutside}>
      <div className="payment-links-modal-container" ref={modalRef} onClick={handleModalContentClick}>
        <div className="payment-links-modal-content">
          <div className="payment-links-modal-header">
            <h2 className="payment-links-modal-title">
              {isGroupDonation ? 'Support this Community' : (isClass ? `Dedicate to ${eventTitle || 'this class'}` : `Sponsor ${eventTitle || 'this event'}`)}
            </h2>
            <button className="payment-links-modal-close-btn" onClick={handleClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {loading ? (
            <div className="payment-links-loading">
              <div className="loading-spinner"></div>
              <p>Loading payment options...</p>
            </div>
          ) : availablePayments.length > 0 ? (
            <>
              {/* Sponsorship Amount Display */}
              {!isGroupDonation && sponsorshipAmount && (
                <div className="sponsorship-amount-section">
                  <div className="sponsorship-amount-label">
                    {isClass ? 'Suggested Dedication Amount' : 'Suggested Sponsorship Amount'}
                  </div>
                  <div className="sponsorship-amount-value">
                    ${sponsorshipAmount}
                  </div>
                  <div className="sponsorship-amount-note">
                    {isClass ? 'Help support by giving the full amount or partially and add a personal dedication' : 'Help support by giving the full amount or partially and add a personal dedication'}
                  </div>
                </div>
              )}
              
              {/* Payment note */}
              <div className="payment-note">
                <p>📝 <strong>Note:</strong> Please specify in your payment what this donation is for</p>
              </div>
              
              <div className="payment-links-list">
                {availablePayments.map(([service, link]) => {
                  const serviceConfig = paymentServices[service];
                  const actionInfo = getActionInfo(link);
                  return (
                    <button
                      key={service}
                      className="payment-link-item"
                      onClick={() => handlePaymentLinkClick(service, link)}
                      style={{ borderColor: serviceConfig.color }}
                    >
                      <div className="payment-link-content">
                        <div className="payment-link-logo">
                          {serviceConfig.logo ? (
                            <img 
                              src={serviceConfig.logo} 
                              alt={serviceConfig.name}
                              className={`payment-logo-img ${service === 'paypal' ? 'paypal-logo' : ''}`}
                            />
                          ) : (
                            <div 
                              className="payment-logo-placeholder"
                              style={{ backgroundColor: serviceConfig.color }}
                            >
                              {serviceConfig.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="payment-link-info">
                          <span className="payment-service-name">{serviceConfig.name}</span>
                          <span className="payment-link-action">{actionInfo.text}</span>
                        </div>
                        <FontAwesomeIcon 
                          icon={actionInfo.icon} 
                          className="payment-link-arrow"
                          style={{ color: serviceConfig.color }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="no-payment-links">
              <p>No payment options are currently available for {isGroupDonation ? 'this community' : (isClass ? 'this class' : 'this event')}.</p>
              <p>Please contact the {isGroupDonation ? 'community leaders' : 'organizer'} for alternative payment methods.</p>
            </div>
          )}

          {copyNotification && (
            <div className="copy-notification">
              {copyNotification}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PaymentLinksModal;